import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { useContext, useState, useEffect, createContext, useCallback, useRef } from "react";
import { supabase } from "../config/supabaseClient";
import { runAuthMutation } from "../Utils/auth/runAuthMutation";
import { supabasePublic } from "../config/supabasePublicClient";

// ─── Context ───────────────────────────────────────────────────────────────

const AuthContext = createContext<{
  isOnboardingFinished: boolean;
  username: string | null;
  session: Session | null;
  user: User | null;
  signOut: () => void;
  currentOnboardingStep: number;
  isAdmin?: boolean;
  adminId?: string | null;
  isMember?: boolean;
  memberId?: string | null;
}>({
  isOnboardingFinished: false,
  username: null,
  session: null,
  user: null,
  signOut: () => {},
  currentOnboardingStep: 1,
  isAdmin: false,
  adminId: null,
  isMember: false,
  memberId: null,
});

// ─── LocalStorage helpers for admin/member modes ────────────────────────────

type AdminMemberState = {
  isAdmin: boolean;
  adminId: string | null;
  isMember: boolean;
  memberId: string | null;
  username: string | null;
  user: User | null;
  handled: boolean;
};

function parseAdminMemberFromStorage(): AdminMemberState {
  const adminDataString = localStorage.getItem("admin");
  const memberDataString = localStorage.getItem("member");
  const loggedInUsername = localStorage.getItem("loggedIn");

  if (adminDataString && loggedInUsername) {
    try {
      const data = JSON.parse(adminDataString);
      if (data?.user_id && data?.email) {
        return {
          isAdmin: true,
          adminId: data.user_id,
          isMember: false,
          memberId: null,
          username: loggedInUsername,
          user: {
            id: data.user_id,
            email: data.email || "",
            user_metadata: {
              username: loggedInUsername,
              name: data.name,
            },
            app_metadata: {},
            aud: "authenticated",
            created_at: "",
            name: data.name,
          } as User,
          handled: true,
        };
      }
    } catch {
      // invalid JSON, ignore
    }
  }

  if (memberDataString) {
    try {
      const data = JSON.parse(memberDataString);
      return {
        isAdmin: false,
        adminId: null,
        isMember: true,
        memberId: data?.user_id ?? null,
        username: loggedInUsername ?? data?.username ?? null,
        user: {
          id: data.user_id,
          email: data.email || "",
          user_metadata: { username: loggedInUsername ?? data?.username, ...data },
          app_metadata: {},
          aud: "authenticated",
          created_at: "",
          name: data.name,
        } as User,
        handled: true,
      };
    } catch {
      // invalid JSON, ignore
    }
  }

  if (loggedInUsername && !adminDataString && !memberDataString) {
    return {
      isAdmin: false,
      adminId: null,
      isMember: false,
      memberId: null,
      username: loggedInUsername,
      user: null,
      handled: false,
    };
  }

  return {
    isAdmin: false,
    adminId: null,
    isMember: false,
    memberId: null,
    username: null,
    user: null,
    handled: false,
  };
}

// ─── Provider ──────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [username, setUsername] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null); // check if the session is not null and available in localStorage, than set the session to the session in localStorage
  const [authBootstrapped, setAuthBootstrapped] = useState(true);
  const sessionRef = useRef<Session | null>(null);
  useEffect(() => {
    const getSession = async () => {
      // Supabase returns { data: { session }, error }. We only want the Session.
      const supabaseRes = await supabase.auth.getSession();
      const supaSession = supabaseRes?.data?.session ?? null;
      if (supaSession) {
        sessionRef.current = supaSession;
        setSession(supaSession);
        return;
      }

      // Fallback: legacy localStorage "session" (stringified Session).
      const stored = localStorage.getItem("session");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          sessionRef.current = parsed;
          setSession(parsed);
        } catch {
          // ignore invalid JSON
        }
      }
    };
    getSession().finally(() => setAuthBootstrapped(true));
  }, []);
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState(1);
  const [isOnboardingFinished, setIsOnboardingFinished] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminId, setAdminId] = useState<string | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);

  const checkLocalStorage = useCallback(() => {
    const parsed = parseAdminMemberFromStorage();
    setIsAdmin(parsed.isAdmin);
    setAdminId(parsed.adminId);
    setIsMember(parsed.isMember);
    setMemberId(parsed.memberId);
    setUsername(parsed.username);
    if (parsed.user) {
      setUser(parsed.user);
    }
    return parsed.handled;
  }, []);

  useEffect(() => {
    // Single source of truth: Supabase onAuthStateChange.
    // When in admin/member mode, do NOT overwrite user with session.user so that
    // user.id stays the page-owner (creator) id and dashboard data doesn’t switch to the logged-in admin/member.
    const { data: sub } = supabase?.auth?.onAuthStateChange((event: AuthChangeEvent, newSession: Session | null) => {
      const parsed = parseAdminMemberFromStorage();

      // Always sync the GoTrue session — never keep a stale JWT in React after
      // SIGNED_OUT / failed refresh (previous `newSession ?? prev` caused that).
      sessionRef.current = newSession;
      setSession(newSession);

      if (parsed.handled) {
        // Member: keep constructed user (creator id). Admin: don’t set user here; effect below sets it from username.
        if (parsed.isMember && parsed.user) {
          setUser(parsed.user);
        }
        return;
      }

      if (newSession?.user) {
        setUser(newSession.user);
        const meta = newSession.user.user_metadata;
        const loggedInUsername = localStorage.getItem("loggedIn");
        if (meta) {
          setCurrentOnboardingStep(meta.currentOnboardingStep ?? 1);
          setUsername(meta.username ?? loggedInUsername);
          setIsOnboardingFinished(meta.isOnboardingFinished ?? false);
        }
      } else if (event === "SIGNED_OUT" && !localStorage.getItem("loggedIn")) {
        // Only clear React identity when this is a real logout / login reset.
        // Idle JWT expiry can SIGNED_OUT while admin/member localStorage remains;
        // keep that overlay so a later refresh can recover without a hard kick.
        setUser(null);
        setUsername(null);
        setIsAdmin(false);
        setAdminId(null);
        setIsMember(false);
        setMemberId(null);
      }
    });

    // Bootstrap admin/member from localStorage (session comes from INITIAL_SESSION)
    const stored = parseAdminMemberFromStorage();
    if (stored.handled) {
      setIsAdmin(stored.isAdmin);
      setAdminId(stored.adminId);
      setIsMember(stored.isMember);
      setMemberId(stored.memberId);
      setUsername(stored.username);
      if (stored.user) setUser(stored.user);
    }

    // Safari / background tabs throttle timers, so auto-refresh can miss the JWT
    // expiry window. Refresh when the tab becomes visible again — but only when
    // a session is plausible. Blind refreshSession on anonymous public pages
    // raced public `.from()` calls on the auth client (AbortError).
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const hasSessionHint =
        Boolean(sessionRef.current) ||
        Boolean(localStorage.getItem("loggedIn")) ||
        Boolean(localStorage.getItem("admin")) ||
        Boolean(localStorage.getItem("member"));
      if (!hasSessionHint) return;
      void runAuthMutation(() =>
        supabase.auth.refreshSession().then((r) => r),
      ).catch(() => {
        // Ignore — getSession / next auth event will reflect failure.
      });
    };
    document.addEventListener("visibilitychange", onVisibility);

    window.addEventListener("memberLogin", checkLocalStorage);
    window.addEventListener("adminLogin", checkLocalStorage);
    window.addEventListener("storage", checkLocalStorage);

    return () => {
      sub?.subscription?.unsubscribe();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("memberLogin", checkLocalStorage);
      window.removeEventListener("adminLogin", checkLocalStorage);
      window.removeEventListener("storage", checkLocalStorage);
    };
  }, []);

  // When in admin mode, user should be the profile for the viewed username (page owner), not the admin.
  // Fetch that profile so user.id is correct everywhere without passing pageOwnerUserId through the tree.
  useEffect(() => {
    if (!isAdmin || !username) return;
    let cancelled = false;
    (async () => {
      // Session-free read — must not contend with GoTrue refresh on the auth client.
      const { data, error } = await supabasePublic
        .from("user")
        .select("user_id, email, name")
        .eq("username", username)
        .maybeSingle();
      if (cancelled || error || !data?.user_id) return;
      setUser({
        id: data.user_id,
        email: data.email ?? "",
        user_metadata: { username },
        app_metadata: {},
        aud: "authenticated",
        created_at: "",
        name: data.name ?? "",
      } as User);
    })();
    return () => { cancelled = true; };
  }, [isAdmin, username]);


  const value = {
    isOnboardingFinished,
    username,
    session,
    user,
    signOut: () => {
      void runAuthMutation(() => supabase.auth.signOut());
    },
    currentOnboardingStep,
    isAdmin,
    adminId,
    isMember,
    memberId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

// ─── Legacy / commented code (for reference) ───────────────────────────────
/*
import { Session, User } from "@supabase/supabase-js";
import { useContext, useState, useEffect, createContext, useRef } from "react";
import { supabase } from "../config/supabaseClient";
import { isAbortError } from "../Utils/errors/isAbortError";

// create a context for authentication
const AuthContext = createContext<{...}>({...});

export const AuthProvider = ({ children }: any) => {
  const [username, setUsername] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  ... hasInitializedRef, pathname checks, checkLocalStorage with getSession(),
  manual refreshSession timer, etc.
};

export const useAuth = () => useContext(AuthContext);
*/
